import { z } from 'zod';

export const syncDataValidationScopeSchema = z
  .object({
    type: z.literal('date_window'),
    basis: z.enum([
      'bubble_created_at',
      'bubble_modified_at',
      'postgres_synced_at',
      'app_domain_date',
    ]),
    from: z.string(),
    to: z.string(),
    label: z.string(),
    evidenceKind: z.enum([
      'source_created_window',
      'source_modified_window',
      'local_sync_window',
      'app_domain_window',
    ]),
    targetColumn: z.string(),
    acceptanceLimitations: z.array(z.string()),
  })
  .nullable();

export const syncDataValidationRunSchema = z.object({
  id: z.string(),
  bubbleKey: z.string(),
  postgresTableName: z.string(),
  requestMode: z.enum(['sample', 'full']),
  sampleLimit: z.number().nullable(),
  status: z.string(),
  dataOutcome: z.string().nullable(),
  cancelRequested: z.boolean(),
  selectedCount: z.number(),
  processedCount: z.number(),
  matchedCount: z.number(),
  findingCount: z.number(),
  warningCount: z.number(),
  executionErrorCount: z.number(),
  lastProgress: z.object({
    at: z.string().nullable(),
    cursorAvailable: z.boolean(),
  }),
  failureCode: z.enum(['run_failed', 'cleanup_failed']).nullable(),
  reportAvailable: z.boolean(),
  reportState: z.enum(['pending', 'available', 'expired', 'cleanup_failed']),
  acceptanceStatus: z
    .object({
      state: z.enum([
        'accepted',
        'incomplete',
        'pending_repair',
        'needs_followup',
        'evidence_unavailable',
        'overflow_blocked',
      ]),
      entryId: z.string().nullable(),
      scopeLabel: z.string().nullable(),
      scopeBasis: z
        .enum(['bubble_created_at', 'bubble_modified_at', 'postgres_synced_at', 'app_domain_date'])
        .nullable(),
      acceptanceLabel: z.enum([
        'scoped_source_window',
        'local_sync_window_only',
        'full_table_candidate',
      ]),
      classificationGranularity: z.enum(['record', 'group', 'overflow_blocked']).nullable(),
      reportState: z.enum(['pending', 'available', 'expired', 'cleanup_failed']),
      canonicalEvidenceRef: z
        .object({
          sha256: z.string(),
        })
        .nullable(),
      blockers: z.array(z.string()),
    })
    .nullable()
    .optional(),
  validationScope: syncDataValidationScopeSchema,
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});

export const syncDataValidationRunsResponseSchema = z.object({
  activeRun: syncDataValidationRunSchema.nullable(),
  recentRuns: z.array(syncDataValidationRunSchema),
  recentRunsPagination: z.object({
    limit: z.number(),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const syncDataValidationTargetSchema = z.object({
  bubbleKey: z.string(),
  postgresTableName: z.string(),
  label: z.string(),
});

export const syncDataValidationTargetsResponseSchema = z.object({
  items: z.array(syncDataValidationTargetSchema),
  fetchedAt: z.string(),
});

export const startSyncDataValidationRunResponseSchema = z.object({
  deduped: z.boolean(),
  jobId: z.string().nullable().optional(),
  run: syncDataValidationRunSchema,
});

export const cancelSyncDataValidationRunResponseSchema = z.object({
  run: syncDataValidationRunSchema,
});

export const syncDataValidationReportResponseSchema = z.object({
  reportState: z.literal('available'),
  signedUrl: z.string(),
  expiresInSeconds: z.number(),
  contentType: z.string().nullable().optional(),
  byteSize: z.number().nullable().optional(),
  run: syncDataValidationRunSchema,
});

export type SyncDataValidationRun = z.infer<typeof syncDataValidationRunSchema>;
export type SyncDataValidationScope = z.infer<typeof syncDataValidationScopeSchema>;
export type SyncDataValidationRunsResponse = z.infer<typeof syncDataValidationRunsResponseSchema>;
export type SyncDataValidationRunsPagination =
  SyncDataValidationRunsResponse['recentRunsPagination'];
export type SyncDataValidationTarget = z.infer<typeof syncDataValidationTargetSchema>;
export type SyncDataValidationTargetsResponse = z.infer<
  typeof syncDataValidationTargetsResponseSchema
>;
export type StartSyncDataValidationRunResponse = z.infer<
  typeof startSyncDataValidationRunResponseSchema
>;
export type CancelSyncDataValidationRunResponse = z.infer<
  typeof cancelSyncDataValidationRunResponseSchema
>;
export type SyncDataValidationReportResponse = z.infer<
  typeof syncDataValidationReportResponseSchema
>;

export type StartSyncDataValidationRunInput = {
  bubbleKey: string;
  limit?: number;
  mode?: 'sample' | 'full';
  confirmFullScan?: boolean;
  idempotencyKey?: string;
  scope?: {
    type: 'date_window';
    basis: 'bubble_created_at' | 'bubble_modified_at' | 'postgres_synced_at' | 'app_domain_date';
    from: string;
    to: string;
    label?: string;
  };
};
